import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';

import PopupResult from '~/src/components/Popups/PopupResult';
import type { TField } from '~/src/components/RequestForm';
import type { IPopupProps } from '~/src/features/router/order-card';
import { initialPopupState, STATE_DEFAULT_INPUTS } from '~/src/features/yandex-landing/constants';
import InputBlock from '~/src/features/yandex-landing/InputBlock';

import type { IFeedbackForm } from './types';

import * as ST from './styled';

const FeedbackForm = forwardRef<HTMLDivElement, IFeedbackForm>(({
    city,
    title,
    TitleComponent,
    subtitle,
    Container,
    withoutName,
    withoutAgreement,
    FormWrapper,
    InputWrapperComponent,
    InputNameComponent,
    InputPhoneComponent,
    ButtonWrapperComponent,
    ButtonComponent,
    buttonTitle,
    namePlaceholder,
    buttonType,
    AdditionTopComponent,
    AdditionBottomComponent,
    agreeAboveButton,
    options,
}, callbackFormRef): JSX.Element => {
    const [phone, setPhone] = useState<TField>(STATE_DEFAULT_INPUTS);
    const [name, setName] = useState<TField>(STATE_DEFAULT_INPUTS);
    const [isValid, setValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [popup, setPopup] = useState<Omit<IPopupProps, 'isOrderOpened'>>({
        ...initialPopupState,
    });

    useEffect(() => {
        setValid((withoutName || name.isValid) && phone.isValid);
    }, [withoutName, name, phone]);

    const closePopup = useCallback(() => {
        name.clearInput();
        phone.clearInput();
        setPopup((state) => ({
            ...state,
            isResultOpened: false,
        }));
    }, [name, phone]);

    const TitleFeedback = useMemo(() => {
        return TitleComponent || ST.TitleFeedback;
    }, [TitleComponent]);

    const ContainerFeedbackForm = useMemo(() => {
        return Container || React.Fragment;
    }, [Container]);

    const FeedbackFormWrapper = useMemo(() => {
        return FormWrapper || React.Fragment;
    }, [FormWrapper]);

    return (
        <ContainerFeedbackForm>
            {!!AdditionTopComponent && AdditionTopComponent}
            <FeedbackFormWrapper ref={callbackFormRef}>
                {(title || subtitle) && <ST.TextFeedbackWrapper>
                    {title && <TitleFeedback>
                        {title}
                    </TitleFeedback>}
                    {subtitle && <ST.SubTextFeedback>
                        {subtitle}
                    </ST.SubTextFeedback>}
                </ST.TextFeedbackWrapper>}
                <InputBlock
                    city={city}
                    setPopup={setPopup}
                    setIsLoading={setIsLoading}
                    phone={phone}
                    name={name}
                    namePlaceholder={namePlaceholder}
                    setPhone={setPhone}
                    setName={setName}
                    isValid={isValid}
                    withoutName={withoutName}
                    withoutAgreement={withoutAgreement}
                    InputWrapperComponent={InputWrapperComponent}
                    InputNameComponent={InputNameComponent}
                    InputPhoneComponent={InputPhoneComponent}
                    ButtonWrapperComponent={ButtonWrapperComponent}
                    ButtonComponent={ButtonComponent}
                    buttonTitle={buttonTitle}
                    buttonType={buttonType}
                    agreeAboveButton={agreeAboveButton}
                    options={options}
                />
                {isLoading && <Throbber position={'absolute'} waitStatus={!isLoading}/>}
            </FeedbackFormWrapper>
            {!!AdditionBottomComponent && AdditionBottomComponent}
            <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
            />
        </ContainerFeedbackForm>
    );
});

FeedbackForm.displayName = 'FeedbackFormWithForwardedRef';
export default FeedbackForm;
